import React, { useState, useRef, useEffect, useLayoutEffect, useMemo, useCallback } from 'react';
import { useParams, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { HiCalendar, HiLocationMarker, HiShare, HiCheck, HiBookmark, HiGlobeAlt, HiDownload, HiX } from 'react-icons/hi';
import { useAnimatedVisibility } from '../../hooks/useAnimatedVisibility';
import LoadingSkeleton from '../../components/ui/LoadingSkeleton/LoadingSkeleton';
import EmptyState from '../../components/ui/EmptyState/EmptyState';
import Button from '../../components/ui/Button/Button';
import Modal from '../../components/ui/Modal/Modal';
import GoogleMap from '../../components/ui/GoogleMap/GoogleMap';
import TicketSelection, { TicketSelectionRef } from '../../components/events/TicketSelection/TicketSelection';
import SponsorshipOffers, { SponsorshipOffersRef } from '../../components/events/SponsorshipOffers/SponsorshipOffers';
import TicketReceipt from '../../components/events/TicketReceipt/TicketReceipt';
import PaymentForm from '../../components/events/PaymentForm/PaymentForm';
import ParticipantAvatars from '../../components/events/ParticipantAvatars/ParticipantAvatars';
import ParticipantListModal from '../../components/events/ParticipantListModal/ParticipantListModal';
import UserTicketsSidebar from '../../components/events/UserTicketsSidebar/UserTicketsSidebar';
import EventCommentsSection from '../../components/events/EventCommentsSection/EventCommentsSection';
import { useEventDetails } from '../../hooks/useEventDetails';
import { useChannels } from '../../hooks/useChannels';
import { useSavedEvents } from '../../hooks/useSavedEvents';
import { useGeneral, useAuth } from '../../contexts';
import Services from '../../services/Services';
import { AttendeeInfo, SoldTicket } from '../../types';
import { formatDateOnly, formatTimeOnly } from '../../utils/dateFormatters';
import { extractEventIdFromUrl, generateCommunityUrl, generateEventAdminUrl, createSlug, normalizeLinkedInUrl, generateEventShareUrl } from '../../utils/urlSlug';
import GrayBanner from '../../components/ui/GrayBanner/GrayBanner';
import ChannelInitialIcon from '../../components/ui/ChannelInitialIcon/ChannelInitialIcon';
import PrivateChannelError from '../../components/ui/PrivateChannelError/PrivateChannelError';
import { downloadEventICS } from '../../utils/icsGenerator';
import { generateGoogleCalendarUrl, generateOutlookCalendarUrl, generateYahooCalendarUrl } from '../../utils/calendarUrls';
import { getPublicToken } from '../../utils/publicToken';
import SEO from '../../components/SEO';
import { generateEventSchema } from '../../utils/seoSchemas';
import { stripHtml, sanitizeHtml, prepareHtmlContent } from '../../utils/htmlUtils';
import styles from './EventDetailPage.module.css';

type TicketFlowStep = 'details' | 'tickets' | 'checkout' | 'payment' | 'sponsorship';
const LCV_TICKET_TYPE = 'togather-join-ticket';

const isLcvJoinTicketType = (type?: string) =>
  (type || '').trim().toLowerCase() === LCV_TICKET_TYPE;

const EventDetailPage: React.FC = () => {
  const { eventPath } = useParams<{ eventPath: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Extract eventID from the URL path (format: event-name-event-id)
  // The event ID is the last segment after the last hyphen
  // Always try to extract from pathname first (works even if eventPath param isn't available)
  // Memoized to prevent unnecessary recalculations and refetches
  const eventID = useMemo(() => {
    // Always try to extract from full pathname first (handles /tickets and /sponsorships)
    // This works even if eventPath param isn't available yet
    const extracted = extractEventIdFromUrl(location.pathname);
    if (extracted) {
      return extracted;
    }
    
    // Fallback: extract from eventPath param if available
    if (eventPath) {
      const parts = eventPath.split('-');
      // Find the last segment that looks like an ID (numeric or alphanumeric)
      // Usually the event ID is at the end
      const fallbackId = parts[parts.length - 1] || eventPath;
      return fallbackId;
    }
    
    return null;
  }, [eventPath, location.pathname]);
  // Check if we're on a problematic route that needs redirect (before any state initialization)
  const path = location.pathname;
  const basePath = eventPath ? `/events/${eventPath}` : '';
  const isOnTicketsRoute = path.endsWith('/tickets');
  const isOnSponsorshipsRoute = path.endsWith('/sponsorships');
  const needsRedirectRef = useRef(eventPath && (isOnTicketsRoute || isOnSponsorshipsRoute) && path !== basePath);
  
  // Update the ref when pathname changes
  useEffect(() => {
    needsRedirectRef.current = eventPath && (isOnTicketsRoute || isOnSponsorshipsRoute) && path !== basePath;
  }, [eventPath, isOnTicketsRoute, isOnSponsorshipsRoute, path, basePath]);
  
  // Initialize currentStep - always start with 'details' to prevent errors
  // This prevents state from being set incorrectly before redirect
  const [currentStep, setCurrentStep] = useState<TicketFlowStep>('details');
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [selectedAttendees, setSelectedAttendees] = useState<AttendeeInfo[]>([]);
  const [totalPrice, setTotalPrice] = useState(0);
  const [isTicketSelectionValid, setIsTicketSelectionValid] = useState(false);
  const [isSponsorshipSelectionValid, setIsSponsorshipSelectionValid] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showFreeTicketConfirmModal, setShowFreeTicketConfirmModal] = useState(false);
  const [showJoinChannelConfirmModal, setShowJoinChannelConfirmModal] = useState(false);
  const [showLeaveChannelConfirmModal, setShowLeaveChannelConfirmModal] = useState(false);
  const [showCalendarDownloadModal, setShowCalendarDownloadModal] = useState(false);
  const calendarModal = useAnimatedVisibility({ isOpen: showCalendarDownloadModal, closeDuration: 200 });
  const [channelJoinedOverride, setChannelJoinedOverride] = useState<boolean | null>(null);
  const [imageLoading, setImageLoading] = useState(true);
  const [userTickets, setUserTickets] = useState<SoldTicket[]>([]);
  const [eventParticipants, setEventParticipants] = useState<SoldTicket[]>([]);
  const [participantsLoading, setParticipantsLoading] = useState(false);
  const [showParticipantModal, setShowParticipantModal] = useState(false);
  const [joiningEvent, setJoiningEvent] = useState(false);
  const { language, toastSuccess, toastError } = useGeneral();
  const { token, user } = useAuth();
  const { getTranslation, language: currentLanguage } = language;
  const isMountedRef = useRef(true);
  
  // Get joined channels to check if user has joined this event's channel
  // Only fetch if user is authenticated (hook handles null token gracefully)
  const { channels: joinedChannels, refresh: refreshChannels } = useChannels({
    token: token || null,
    userID: user?.id,
    popular: false,
  });
  
  const sponsorshipSectionRef = useRef<HTMLDivElement>(null);
  const ticketsSectionRef = useRef<HTMLDivElement>(null);
  const channelTitleRef = useRef<HTMLHeadingElement>(null);
  const [isChannelTitleTwoLines, setIsChannelTitleTwoLines] = useState(false);
  const lastPathRef = useRef<string>(''); // Track last path to detect external URL changes
  const ticketSelectionRef = useRef<TicketSelectionRef>(null);
  const sponsorshipOffersRef = useRef<SponsorshipOffersRef>(null);
  const hasRedirectedRef = useRef(false); // Track if we've redirected to prevent loops
  const isSyncingFromUrlRef = useRef(false); // Track if we're syncing step from URL (browser navigation)

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Redirect to base event details page if user refreshes on /tickets or /sponsorships routes
  // This MUST run before any other state updates to prevent errors
  // Use useLayoutEffect to run synchronously before browser paint
  // Only redirect on page refresh (when navigation type is not 'POP'), not on browser back/forward
  useLayoutEffect(() => {
    if (!eventPath || !needsRedirectRef.current) {
      // Reset redirect flag when we're not on a problematic route
      if (!needsRedirectRef.current && hasRedirectedRef.current) {
        hasRedirectedRef.current = false;
      }
      return;
    }
    
    // Only redirect once per mount and only if this is a page refresh (not browser navigation)
    // Check if this is browser navigation by seeing if we have a previous path
    // Browser back/forward navigation will have lastPathRef set, page refresh won't
    if (hasRedirectedRef.current) return;
    
    // Only redirect on page refresh, not on browser back/forward navigation
    // If lastPathRef is empty, it's likely a page refresh
    // If lastPathRef is set, it's likely browser navigation and we should not redirect
    if (lastPathRef.current !== '') {
      // This is browser navigation, not a refresh - don't redirect
      needsRedirectRef.current = false;
      return;
    }
    
    // Redirect immediately to prevent any state updates (only on page refresh)
    hasRedirectedRef.current = true;
    navigate(basePath, { replace: true });
  }, [eventPath, basePath, navigate]);

  // Check for section query parameter on mount (for redirects after login)
  useEffect(() => {
    if (!eventID || !eventPath) return;
    
    const section = searchParams.get('section');
    if (section === 'tickets' && currentStep === 'details') {
      setCurrentStep('tickets');
      // Remove query parameter to clean up URL
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('section');
      setSearchParams(newSearchParams, { replace: true });
    } else if (section === 'sponsorships' && currentStep === 'details') {
      setCurrentStep('sponsorship');
      // Remove query parameter to clean up URL
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('section');
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [searchParams, eventID, eventPath, currentStep, setSearchParams]);

  // Sync current step with URL on mount and when URL changes (from browser navigation)
  useEffect(() => {
    if (!eventID || !eventPath) return;
    
    // Don't sync if we're redirecting - wait for redirect to complete
    if (hasRedirectedRef.current && needsRedirectRef.current) {
      return;
    }
    
    const path = location.pathname;
    const basePath = `/events/${eventPath}`;
    
    // If path hasn't changed, skip (prevents unnecessary updates)
    if (path === lastPathRef.current) {
      return;
    }
    
    // Skip syncing step if we need to redirect (prevents state updates before redirect)
    if (needsRedirectRef.current && (path.endsWith('/tickets') || path.endsWith('/sponsorships'))) {
      lastPathRef.current = path; // Update last path to prevent re-running
      return;
    }
    
    // Determine what step the URL represents
    let targetStep: TicketFlowStep | null = null;
    if (path.endsWith('/tickets')) {
      targetStep = 'tickets';
    } else if (path.endsWith('/sponsorships')) {
      targetStep = 'sponsorship';
    } else if (path === basePath || path === `${basePath}/`) {
      targetStep = 'details';
    }
    
    // Only update step if it's different and not payment (payment keeps current context)
    if (targetStep && currentStep !== targetStep && currentStep !== 'payment') {
      // Update last path before state change to prevent loops
      lastPathRef.current = path;
      // Mark that we're syncing from URL (browser navigation)
      isSyncingFromUrlRef.current = true;
      setCurrentStep(targetStep);
      // Reset the flag after state update
      setTimeout(() => {
        isSyncingFromUrlRef.current = false;
      }, 0);
    } else {
      // Update last path even if no state change
      lastPathRef.current = path;
    }
  }, [location.pathname, eventID, eventPath, currentStep]);

  // Update URL when step changes programmatically (not from URL navigation)
  useEffect(() => {
    if (!eventID || !eventPath) return;
    
    const basePath = `/events/${eventPath}`;
    const currentPath = location.pathname;
    
    // Check if URL already matches the step
    const urlMatchesStep = 
      (currentStep === 'tickets' && currentPath.endsWith('/tickets')) ||
      (currentStep === 'sponsorship' && currentPath.endsWith('/sponsorships')) ||
      (currentStep === 'details' && (currentPath === basePath || currentPath === `${basePath}/`));
    
    // If URL already matches, update lastPathRef and return early
    // This prevents unnecessary navigation during browser back/forward
    if (urlMatchesStep) {
      lastPathRef.current = currentPath;
      return;
    }
    
    // Only update URL if step changed programmatically (not from URL sync)
    // Skip if this is a payment step (keeps context of where we came from)
    // Skip if we're currently syncing from URL (browser navigation)
    if (currentStep !== 'payment' && !isSyncingFromUrlRef.current && !urlMatchesStep) {
      // Step changed programmatically, update URL
      let newPath = '';
      if (currentStep === 'tickets') {
        newPath = `${basePath}/tickets`;
        // Add to history when going to tickets (so back button works)
        navigate(newPath);
      } else if (currentStep === 'sponsorship') {
        newPath = `${basePath}/sponsorships`;
        // Add to history when going to sponsorships (so back button works)
        navigate(newPath);
      } else if (currentStep === 'details') {
        newPath = basePath;
        // Use replace when going back to details to avoid cluttering history
        navigate(newPath, { replace: true });
      }
      
      // Update last path ref to prevent loop
      if (newPath) {
        lastPathRef.current = newPath;
      }
    } else if (urlMatchesStep) {
      // URL matches step, just update the ref
      lastPathRef.current = currentPath;
    }
    // Note: We don't update URL for 'payment' step to keep the context of where we came from
  }, [currentStep, eventID, eventPath, navigate, location.pathname]);

  // Scroll to appropriate section when step changes
  useEffect(() => {
    // Check if an input field is currently focused - if so, don't scroll
    const activeElement = document.activeElement;
    const isInputFocused = activeElement && (
      activeElement.tagName === 'INPUT' ||
      activeElement.tagName === 'TEXTAREA' ||
      activeElement.tagName === 'SELECT' ||
      activeElement.getAttribute('contenteditable') === 'true'
    );

    // Don't scroll if an input is focused to prevent interrupting user input
    if (isInputFocused) {
      return;
    }

    if (currentStep === 'sponsorship' && sponsorshipSectionRef.current) {
      setTimeout(() => {
        sponsorshipSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } else if (currentStep === 'tickets' && ticketsSectionRef.current) {
      setTimeout(() => {
        ticketsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } else if (currentStep === 'payment') {
      // For payment, scroll to the section based on which view we came from
      const sectionRef = selectedAttendees.length > 0 && selectedAttendees[0]?.ticketType?.toLowerCase() === 'sponsor'
        ? sponsorshipSectionRef.current
        : ticketsSectionRef.current;
      if (sectionRef) {
        setTimeout(() => {
          sectionRef.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    } else if (currentStep === 'details') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentStep, selectedAttendees]);

  // Use empty string for token when not authenticated - hook will use public token
  const { event, loading, error, participantCount, refreshEvent } = useEventDetails({
    token: token || null,
    eventID: eventID ? String(eventID) : '',
    loadingKey: 'event.details'
  });

  // Sort speakers alphabetically by full name
  const sortedSpeakers = useMemo(() => {
    if (!event?.speakers) return [];
    return [...event.speakers].sort((a, b) => {
      const nameA = `${a.name || ''} ${a.lastname || ''}`.trim().toLowerCase();
      const nameB = `${b.name || ''} ${b.lastname || ''}`.trim().toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [event?.speakers]);

  // Detect if channel title wraps to 2 lines → use wider icon (100px) for balance
  useEffect(() => {
    const el = channelTitleRef.current;
    if (!el) return;
    const checkLines = () => {
      const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 25.2;
      const twoLineThreshold = lineHeight * 1.5;
      setIsChannelTitleTwoLines(el.scrollHeight > twoLineThreshold);
    };
    checkLines();
    const ro = new ResizeObserver(checkLines);
    ro.observe(el);
    return () => ro.disconnect();
  }, [event?.channelDetails?.title, event?.channelTitle]);

  // Saved events hook (only works when authenticated)
  const { toggleBookmark, isEventSaved, fetchSavedEventIds } = useSavedEvents({
    token: token || null,
    userID: user?.id,
    autoFetch: false
  });

  // Check if event is saved - recalculate when event or saved state changes
  const isSaved = useMemo(() => {
    return event ? isEventSaved(event.eventID) : false;
  }, [event, isEventSaved]);

  // Refresh saved event IDs when event loads
  useEffect(() => {
    if (event?.eventID && user?.id && token) {
      fetchSavedEventIds();
    }
  }, [event?.eventID, user?.id, token, fetchSavedEventIds]);

  // Fetch user's tickets for this event
  const fetchUserTickets = useCallback(async () => {
    if (!event?.eventID || !user?.id || !token) {
      setUserTickets([]);
      return;
    }

    try {
      const response = await Services.SocialHiveGetEventMyTickets(token, event.eventID, user.id);
      if (response.data.status && response.data.result) {
        // Service now filters by eventID, so all returned tickets are for this event
        setUserTickets(response.data.result);
      } else {
        setUserTickets([]);
      }
    } catch (error) {
      // Silently fail - if we can't fetch tickets, assume user has none
      setUserTickets([]);
    }
  }, [event?.eventID, user?.id, token]);

  useEffect(() => {
    fetchUserTickets();
  }, [fetchUserTickets]);

  // Fetch event participants (sold tickets)
  const fetchEventParticipants = useCallback(async () => {
    if (!event?.eventID) {
      setEventParticipants([]);
      return;
    }

    setParticipantsLoading(true);
    try {
      // Use user token if authenticated, otherwise use public token
      const authToken = token || await getPublicToken();

      if (!authToken) {
        setEventParticipants([]);
        setParticipantsLoading(false);
        return;
      }

      const response = await Services.SocialHiveGetEventSoldTickets(authToken, event.eventID);
      if (response.data.status && response.data.result) {
        setEventParticipants(response.data.result);
      } else {
        setEventParticipants([]);
      }
    } catch (error) {
      // Silently fail - if we can't fetch participants, show empty state
      setEventParticipants([]);
    } finally {
      setParticipantsLoading(false);
    }
  }, [event?.eventID, token]);

  useEffect(() => {
    fetchEventParticipants();
  }, [fetchEventParticipants]);

  // Check if user has at least 1 ticket for this specific event
  // Service now filters by eventID, so if we have any tickets, they're for this event
  const hasUserTickets = useMemo(() => {
    return userTickets.length > 0;
  }, [userTickets]);

  const customTickets = useMemo(() => {
    if (!event || !Array.isArray(event.tickets)) return [];
    return event.tickets.filter((ticket) => {
      const ticketType = (ticket.type || '').trim().toLowerCase();
      return ticketType !== 'sponsor' && !isLcvJoinTicketType(ticketType);
    });
  }, [event]);

  const joinEventTicket = useMemo(() => {
    if (!event || !Array.isArray(event.tickets)) return null;
    return event.tickets.find((ticket) => isLcvJoinTicketType(ticket.type)) || null;
  }, [event]);
  const isRsvpEvent = useMemo(() => Boolean(joinEventTicket) && customTickets.length === 0, [joinEventTicket, customTickets.length]);

  // Check if user has joined the event's channel and if they're admin
  const { isChannelJoined, isChannelAdmin } = useMemo(() => {
    if (!event?.channelID) return { isChannelJoined: false, isChannelAdmin: false };
    const userChannel = joinedChannels.find(
      (ch) => String(ch.channelID) === String(event.channelID)
    );
    const joined = channelJoinedOverride !== null ? channelJoinedOverride : !!userChannel;
    return {
      isChannelJoined: joined,
      isChannelAdmin: userChannel ? String(userChannel.isAdmin) === "1" : false
    };
  }, [joinedChannels, event?.channelID, channelJoinedOverride]);

  // Check if channel is private and user is not a member
  const isPrivateChannelAndNotMember = useMemo(() => {
    if (!event) return false;
    // If we can't determine if it's public, assume it's accessible (conservative approach)
    if (event.isPublic === undefined) {
      return false;
    }
    // Unlisted events (isPublic "3") are always accessible via direct link
    if (event.isPublic === "3") {
      return false;
    }
    // Channel is private if isPublic is "0"
    const isPrivate = event.isPublic === "0";
    return isPrivate && !isChannelJoined;
  }, [event, isChannelJoined]);

  const handleEditEvent = () => {
    if (!event?.eventID || !event?.channelID) return;
    
    // Navigate to event admin page
    // If user is not admin, OrganizerPanelPage will show unauthorized page
    const channelTitle = event.channelDetails?.title || event.channelTitle;
    if (!channelTitle) return;
    const channelSlug = createSlug(channelTitle);
    const evtTitle = event.eventTitle || event.title;
    const adminUrl = generateEventAdminUrl(evtTitle, event.eventID, channelSlug);
    navigate(adminUrl);
  };

  const handleJoinChannel = () => {
    setShowJoinChannelConfirmModal(true);
  };

  const handleLeaveChannel = () => {
    setShowLeaveChannelConfirmModal(true);
  };

  const confirmJoinChannel = async () => {
    if (!token || !user?.id || !event?.channelID) {
      toastError(getTranslation('communities.joinError'));
      setShowJoinChannelConfirmModal(false);
      return;
    }

    try {
      const response = await Services.SocialHiveJoinToChannel(token, user.id, event.channelID);
      if (response.data.status) {
        // Check if it was a join request (for private channels) or direct join
        const responseMessage = response.data.message || '';
        const isJoinRequest = responseMessage.toLowerCase().includes('request') || 
          responseMessage.toLowerCase().includes('pending') ||
          responseMessage.toLowerCase().includes('approval');
        
        if (isJoinRequest) {
          toastSuccess(getTranslation('communities.joinRequestSent') || getTranslation('communities.joinRequestSuccess'));
        } else {
          toastSuccess(getTranslation('communities.joinSuccess'));
        }
        
        setShowJoinChannelConfirmModal(false);
        // Optimistically update join state so UI reflects immediately
        if (!isJoinRequest) {
          setChannelJoinedOverride(true);
        }
        // Refresh event details to update channel info
        if (refreshEvent) {
          refreshEvent();
        }
        refreshChannels();
      } else {
        toastError(response.data.message || getTranslation('communities.joinError'));
        setShowJoinChannelConfirmModal(false);
      }
    } catch (error: any) {
      toastError(error.response?.data?.message || getTranslation('communities.joinError'));
      setShowJoinChannelConfirmModal(false);
    }
  };

  const confirmLeaveChannel = async () => {
    if (!token || !user?.id || !event?.channelID) {
      toastError(getTranslation('communities.leaveError'));
      setShowLeaveChannelConfirmModal(false);
      return;
    }

    try {
      const response = await Services.SocialHiveLeaveChannel(token, user.id, event.channelID);
      if (response.data.status) {
        toastSuccess(getTranslation('communities.leaveSuccess'));
        setShowLeaveChannelConfirmModal(false);
        // Optimistically update join state so UI reflects immediately
        setChannelJoinedOverride(false);
        // Refresh event details to update channel info
        if (refreshEvent) {
          refreshEvent();
        }
        refreshChannels();
      } else {
        toastError(response.data.message || getTranslation('communities.leaveError'));
        setShowLeaveChannelConfirmModal(false);
      }
    } catch (error: any) {
      toastError(error.response?.data?.message || getTranslation('communities.leaveError'));
      setShowLeaveChannelConfirmModal(false);
    }
  };

  // Check if event has ended
  const isEventEnded = useMemo(() => {
    if (!event || !event.eventEndDateTime) return false;
    const now = Date.now();
    const eventEnd = Number(event.eventEndDateTime) * 1000;
    return now > eventEnd;
  }, [event]);

  const isParticipationOpen = useMemo(() => {
    if (!event) return false;
    if (event.hasTickets !== "1") return false;

    const now = Date.now();
    const saleStart = event.ticketSaleStartDateTime ? Number(event.ticketSaleStartDateTime) * 1000 : 0;
    const eventEnd = event.eventEndDateTime ? Number(event.eventEndDateTime) * 1000 : Infinity;
    const withinSaleWindow = now >= saleStart && now <= eventEnd;

    if (!withinSaleWindow) return false;

    return isRsvpEvent ? Boolean(joinEventTicket) : true;
  }, [event, isRsvpEvent, joinEventTicket]);

  useEffect(() => {
    if (isRsvpEvent && currentStep === 'tickets') {
      setCurrentStep('details');
    }
  }, [isRsvpEvent, currentStep]);

  const handleGetTickets = () => {
    if (isRsvpEvent) {
      return;
    }

    // Check if user is authenticated
    if (!token || !user) {
      toastError(getTranslation('home.loginRequired') || 'Please login to get tickets');
      // Redirect to login with event page URL and section query parameter
      const basePath = `/events/${eventPath}`;
      const redirectUrl = encodeURIComponent(`${basePath}?section=tickets`);
      navigate(`/login?redirect=${redirectUrl}`);
      return;
    }

    if (!isParticipationOpen) {
      alert(getTranslation('events.notOnSale'));
      return;
    }
    setCurrentStep('tickets');
  };

  const handleJoinEvent = async () => {
    if (!event || !joinEventTicket) return;

    if (!token || !user) {
      toastError(getTranslation('home.loginRequired') || 'Please login to join the event');
      const basePath = `/events/${eventPath}`;
      const redirectUrl = encodeURIComponent(basePath);
      navigate(`/login?redirect=${redirectUrl}`);
      return;
    }

    if (!isParticipationOpen || isEventEnded) {
      toastError(
        isRsvpEvent
          ? (getTranslation('events.participationNotOpen') || 'Participation is not open yet')
          : (getTranslation('events.notOnSale') || 'Tickets are not on sale')
      );
      return;
    }

    if (hasUserTickets) {
      return;
    }

    const name = (user.name || '').trim();
    const lastname = (user.lastname || '').trim();
    const email = (user.email || '').trim();

    if (!name || !lastname || !email) {
      toastError(getTranslation('events.userInfoMissing') || 'Name, lastname and email are required.');
      return;
    }

    setJoiningEvent(true);
    try {
      let phone = String((user as any)?.phone || '').trim();

      if (!phone) {
        try {
          const profileResponse = await Services.GetAnyUserInfos(token, user.id);
          if (profileResponse.data.status && profileResponse.data.result?.phone) {
            phone = String(profileResponse.data.result.phone).trim();
          }
        } catch {
          // Phone is optional; continue without it if profile fetch fails.
        }
      }

      const response = await Services.SocialHiveCreateSoldTicket(
        token,
        event.channelID,
        event.eventID,
        joinEventTicket.ticketID,
        "free",
        name,
        lastname,
        email,
        phone,
        true,
        {}
      );

      if (!response.data.status) {
        throw new Error(response.data.message || getTranslation('events.somethingWentWrong'));
      }

      if (response.data.result) {
        setUserTickets((prev) => (prev.length > 0 ? prev : [response.data.result]));
      }

      toastSuccess(getTranslation('events.joinedEventSuccess') || 'You joined the event successfully.');

      await Promise.all([fetchUserTickets(), fetchEventParticipants()]);
      if (refreshEvent) {
        refreshEvent();
      }
    } catch (error: any) {
      toastError(error?.response?.data?.message || error?.message || getTranslation('events.somethingWentWrong'));
    } finally {
      setJoiningEvent(false);
    }
  };



  const handleFreeTicketsPurchase = async () => {
    if (!token || !event) return;

    // Close confirmation modal
    setShowFreeTicketConfirmModal(false);

    try {
      // Create all free tickets - check each ticket price to determine if free
      const promises = selectedAttendees.map(attendee =>
        Services.SocialHiveCreateSoldTicket(
          token,
          attendee.channelID,
          attendee.eventID,
          attendee.ticketID,
          attendee.ticketPrice === 0 ? "free" : "free", // All are free if totalPrice is 0
          attendee.name,
          attendee.lastname,
          attendee.email,
          attendee.phone,
          true,
          {}
        )
      );

      await Promise.all(promises);
      
      // Show success and reset
      setShowSuccessModal(true);
      setCurrentStep('details');
      setSelectedAttendees([]);
      setTotalPrice(0);
      
      // Refresh event details
      if (refreshEvent) {
        refreshEvent();
      }
    } catch (error: any) {
      alert(error.message || getTranslation('events.somethingWentWrong'));
    }
  };

  const handlePaymentSuccess = async (paymentID: string, billing: Record<string, any>) => {
    if (!token || !event) return;

    try {
      // Create all paid tickets - following the guide exactly
      const promises = selectedAttendees.map((attendee, index) => {
        return Services.SocialHiveCreateSoldTicket(
          token,
          attendee.channelID,
          attendee.eventID,
          attendee.ticketID,
          paymentID,
          attendee.name,
          attendee.lastname,
          attendee.email,
          attendee.phone,
          true, // isPaid
          billing
        );
      });

      await Promise.all(promises);
      
      // Show success and reset
      setShowSuccessModal(true);
      setCurrentStep('details');
      setSelectedAttendees([]);
      setTotalPrice(0);
      
      // Refresh event details and user tickets
      if (refreshEvent) {
        refreshEvent();
      }
      await fetchUserTickets();
    } catch (error: any) {
      alert(error.response?.data?.message || error.message || getTranslation('events.somethingWentWrong'));
    }
  };

  const handlePaymentError = (errorMessage: string) => {
    alert(errorMessage);
  };

  const handleBackToDetails = () => {
    setCurrentStep('details');
    setSelectedAttendees([]);
    setTotalPrice(0);
  };

  const handleDownloadICSClick = () => {
    setShowCalendarDownloadModal(true);
  };

  const handleConfirmDownloadICS = () => {
    if (!event) return;
    const eventUrl = window.location.href;
    const locationLabel = getTranslation('events.eventLocation');
    downloadEventICS(event, eventUrl, locationLabel);
    toastSuccess(getTranslation('events.calendarEventDownloaded'));
    setShowCalendarDownloadModal(false);
  };

  const handleGoogleCalendar = () => {
    if (!event) return;
    const eventUrl = window.location.href;
    const url = generateGoogleCalendarUrl(event, eventUrl);
    window.open(url, '_blank', 'noopener,noreferrer');
    setShowCalendarDownloadModal(false);
  };

  const handleOutlookCalendar = () => {
    if (!event) return;
    const eventUrl = window.location.href;
    const url = generateOutlookCalendarUrl(event, eventUrl);
    window.open(url, '_blank', 'noopener,noreferrer');
    setShowCalendarDownloadModal(false);
  };

  const handleYahooCalendar = () => {
    if (!event) return;
    const eventUrl = window.location.href;
    const url = generateYahooCalendarUrl(event, eventUrl);
    window.open(url, '_blank', 'noopener,noreferrer');
    setShowCalendarDownloadModal(false);
  };

  // Show loading if we're loading OR if we have an eventID but no event yet (initial load)
  if (loading || (eventID && !event && !error)) {
    return (
      <>
        <SEO title="Loading Event" noindex />
        <div className={styles.eventDetailPage}>
          <LoadingSkeleton width="100%" height={400} />
        </div>
      </>
    );
  }

  // Only show "not found" if we have an error or if we've finished loading but have no event
  if (error || (!event && !loading)) {
    return (
      <>
        <SEO title={getTranslation('events.eventNotFound')} description={error || undefined} noindex />
        <div className={styles.eventDetailPage}>
          <EmptyState title={getTranslation('events.eventNotFound')} description={error || ''} />
        </div>
      </>
    );
  }

  // Type guard: at this point, event must exist (TypeScript doesn't understand the logic above)
  if (!event) {
    return (
      <>
        <SEO title="Loading Event" noindex />
        <div className={styles.eventDetailPage}>
          <LoadingSkeleton width="100%" height={400} />
        </div>
      </>
    );
  }

  // Show private channel error if channel is private and user is not a member
  if (isPrivateChannelAndNotMember) {
    return (
      <>
        <SEO title={(event.eventTitle || event.title || getTranslation('events.eventNotFound')).replace(/\\"/g, '"')} noindex />
        <div className={styles.eventDetailPage}>
          <PrivateChannelError
            channelID={event.channelID}
            channelName={event.channelDetails?.title || event.channelTitle}
          />
        </div>
      </>
    );
  }

  const eventTitle = (event.eventTitle || event.title || '').replace(/\\"/g, '"');
  const imageUrl = event.cover || event.bannerImage || event.image || null;
  const hasBannerImage = !!imageUrl;
  const locationDefinition = event.locationAddressDefinition || event.locationAddress || "";
  const descriptionPlainText = stripHtml(event.description || '');
  const isLongDescription = descriptionPlainText.length > 500;

  const canonicalUrl =
    typeof window !== 'undefined' && basePath ? `${window.location.origin}${basePath}` : undefined;

  const organizerName = event.channelDetails?.title || event.channelTitle;
  const organizerUrl =
    typeof window !== 'undefined' && organizerName && event.channelID
      ? `${window.location.origin}${generateCommunityUrl(organizerName, event.channelID)}`
      : undefined;

  const seoDescription =
    descriptionPlainText
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 160) + (descriptionPlainText.trim().length > 160 ? '…' : '');

  const structuredData = canonicalUrl
    ? generateEventSchema({
        event,
        url: canonicalUrl,
        imageUrl: imageUrl || undefined,
        organizerName,
        organizerUrl,
      })
    : undefined;

  // Parse coordinates for map
  const parseCoordinate = (coord: string | undefined): number | null => {
    if (!coord) return null;
    const parsed = parseFloat(coord);
    return isNaN(parsed) ? null : parsed;
  };

  const latitude = parseCoordinate(event.locationLatitude);
  const longitude = parseCoordinate(event.locationLongitude);
  const hasCoordinates = latitude !== null && longitude !== null && !isNaN(latitude) && !isNaN(longitude);

  const showTicketsView = !isRsvpEvent && (
    currentStep === 'tickets' || (currentStep === 'payment' && selectedAttendees.length > 0 && selectedAttendees[0]?.ticketType?.toLowerCase() !== 'sponsor')
  );
  const showSponsorshipView = currentStep === 'sponsorship' || (currentStep === 'payment' && selectedAttendees.length > 0 && selectedAttendees[0]?.ticketType?.toLowerCase() === 'sponsor');
  const canViewComments = !!token;
  const participationCardTitle = isEventEnded
    ? getTranslation('events.eventEnded')
    : isRsvpEvent
      ? (getTranslation('events.rsvpAvailable') || 'LCV Available')
      : getTranslation('events.ticketsAvailable');
  const participationMessage = isEventEnded
    ? getTranslation('events.followChannelForMoreEvents', {
        channelName: event?.channelDetails?.title || event?.channelTitle || ''
      })
    : isRsvpEvent
      ? (getTranslation('events.joinEventMessage') || 'Join this event directly with one click.')
      : getTranslation('events.getTicketsMessage');
  const participationActionLabel = isEventEnded
    ? getTranslation('events.eventEnded')
    : isRsvpEvent
      ? (hasUserTickets
        ? (getTranslation('events.joined') || 'Joined')
        : (joiningEvent
          ? (getTranslation('events.joiningEvent') || 'Joining...')
          : (getTranslation('events.joinEvent') || 'Join Event')))
      : (hasUserTickets
        ? getTranslation('events.getMoreTickets')
        : getTranslation('events.getTickets'));
  const isParticipationActionDisabled =
    isEventEnded || !isParticipationOpen || (isRsvpEvent && (hasUserTickets || joiningEvent));
  const handleParticipationAction = isRsvpEvent ? handleJoinEvent : handleGetTickets;

  return (
    <>
      <SEO
        title={eventTitle}
        description={seoDescription}
        image={imageUrl || undefined}
        url={canonicalUrl}
        type="event"
        structuredData={structuredData}
        lang={currentLanguage}
      />
      <div className={styles.eventDetailPage}>
        <div className={styles.mainContent}>
        {/* Event Header - Banner */}
        <div className={styles.header}>
          <div className={styles.bannerContainer}>
            {hasBannerImage ? (
              <>
                {imageLoading && <div className="image-shimmer" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, borderRadius: 'inherit' }} />}
                <img 
                  src={imageUrl} 
                  alt={event.title}
                  className={styles.bannerImage}
                  onLoad={() => setImageLoading(false)}
                  onError={() => {
                    setImageLoading(false);
                  }}
                />
              </>
            ) : (
              <GrayBanner className={styles.bannerImage} id={event.eventID} />
            )}
          </div>
        </div>

        {/* Tickets Section - Shown when user clicks "Get Tickets" */}
        {showTicketsView && customTickets.length > 0 && (
          <div className={`${styles.section} ${styles.ticketsSection}`} ref={ticketsSectionRef}>
            {currentStep === 'tickets' && (
              <>
                <div className={styles.ticketsHeader}>
                  <h2 className={styles.sectionTitle}>{getTranslation('events.tickets')}</h2>
                  <Button
                    variant="ghost"
                    size="small"
                    onClick={handleBackToDetails}
                    className={styles.backButton}
                  >
                    {getTranslation('common.back')}
                  </Button>
                </div>
                <TicketSelection
                  ref={ticketSelectionRef}
                  tickets={customTickets}
                  channelID={event.channelID}
                  eventID={event.eventID}
                  userInfo={user ? {
                    name: user.name,
                    lastname: user.lastname,
                    email: user.email,
                    phone: ''
                  } : undefined}
                  onCheckout={(attendees, price) => {
                    // Update selected attendees and price when tickets are selected/changed
                    // Ensure price is always a number
                    const numericPrice = typeof price === 'number' ? price : Number(price) || 0;
                    setSelectedAttendees(attendees);
                    setTotalPrice(numericPrice);
                  }}
                  onValidityChange={setIsTicketSelectionValid}
                  getTranslation={getTranslation}
                />
              </>
            )}

            {currentStep === 'payment' && totalPrice > 0 && user && (
              <>
                <div className={styles.ticketsHeader}>
                  <h2 className={styles.sectionTitle}>{getTranslation('events.paymentInformation')}</h2>
                  <Button
                    variant="ghost"
                    size="small"
                    onClick={() => setCurrentStep('tickets')}
                    className={styles.backButton}
                  >
                    {getTranslation('common.back')}
                  </Button>
                </div>
                <PaymentForm
                  attendees={selectedAttendees}
                  totalPrice={totalPrice}
                  currency="TL"
                  userID={user.id}
                  userInfo={{
                    name: user.name,
                    lastname: user.lastname,
                    email: user.email,
                    phone: selectedAttendees.length > 0 ? selectedAttendees[0].phone : ''
                  }}
                  onPaymentSuccess={handlePaymentSuccess}
                  onPaymentError={handlePaymentError}
                  getTranslation={getTranslation}
                />
              </>
            )}
          </div>
        )}

        {/* Mobile Summary Section - Shown when tickets are selected */}
        {showTicketsView && currentStep === 'tickets' && selectedAttendees.length > 0 && (
          <div className={`${styles.section} ${styles.mobileSummarySection}`}>
            <div className={styles.infoCard}>
              <h3 className={styles.infoCardTitle}>{getTranslation('events.orderSummary')}</h3>
              <div className={styles.summaryInfo}>
                <div className={styles.summaryRow}>
                  <span>{getTranslation('events.totalTickets')}:</span>
                  <span className={styles.summaryValue}>{selectedAttendees.length}</span>
                </div>
                <div className={styles.summaryRow}>
                  <span>{getTranslation('events.totalPrice')}:</span>
                  <span className={styles.summaryValue}>
                    {(() => {
                      const numericPrice = typeof totalPrice === 'number' ? totalPrice : Number(totalPrice) || 0;
                      return numericPrice === 0 
                        ? getTranslation('events.free')
                        : `${numericPrice.toLocaleString('en-US')} TL`;
                    })()}
                  </span>
                </div>
              </div>
              <Button
                variant="primary"
                size="large"
                onClick={() => {
                  // Validate attendees before proceeding
                  if (ticketSelectionRef.current) {
                    const validation = ticketSelectionRef.current.validate();
                    if (!validation.ok) {
                      // Validation errors are already shown in the component
                      if (validation.msg) {
                        toastError(validation.msg);
                      }
                      return;
                    }
                  }
                  
                  // Check if we have attendees selected
                  if (selectedAttendees.length === 0) {
                    toastError(getTranslation('events.fillAllFields'));
                    return;
                  }
                  
                  // Ensure totalPrice is a number for comparison
                  const numericPrice = typeof totalPrice === 'number' ? totalPrice : Number(totalPrice) || 0;
                  
                  if (numericPrice === 0) {
                    // Free tickets - show confirmation modal directly (skip checkout step)
                    setShowFreeTicketConfirmModal(true);
                  } else {
                    // Paid tickets - go directly to payment step (skip checkout)
                    setCurrentStep('payment');
                  }
                }}
                className={styles.checkoutButton}
                disabled={(() => {
                  // Check if we have attendees
                  if (selectedAttendees.length === 0) {
                    return true;
                  }

                  return !isTicketSelectionValid;
                })()}
              >
                {(typeof totalPrice === 'number' ? totalPrice : Number(totalPrice) || 0) === 0
                  ? getTranslation('events.confirmPurchase')
                  : getTranslation('events.proceedToPayment')
                }
              </Button>
            </div>
          </div>
        )}

        {/* Sponsorship Offers Section - Shown when user clicks "Become a Sponsor" */}
        {showSponsorshipView && event.tickets && Array.isArray(event.tickets) && event.tickets.length > 0 && (
          <div className={`${styles.section} ${styles.sponsorshipSection}`} ref={sponsorshipSectionRef}>
            {currentStep === 'sponsorship' && (
              <>
                <div className={styles.ticketsHeader}>
                  <h2 className={styles.sectionTitle}>{getTranslation('events.sponsorshipOffers')}</h2>
                  <Button
                    variant="ghost"
                    size="small"
                    onClick={handleBackToDetails}
                    className={styles.backButton}
                  >
                    {getTranslation('common.back')}
                  </Button>
                </div>
                <SponsorshipOffers
                  ref={sponsorshipOffersRef}
                  tickets={event.tickets || []}
                  channelID={event.channelID}
                  eventID={event.eventID}
                  userInfo={user ? {
                    name: user.name,
                    lastname: user.lastname,
                    email: user.email,
                    phone: ''
                  } : undefined}
                  onCheckout={(attendees, price) => {
                    // Update selected attendees and price when sponsorship offers are selected/changed
                    // Ensure price is always a number
                    const numericPrice = typeof price === 'number' ? price : Number(price) || 0;
                    setSelectedAttendees(attendees);
                    setTotalPrice(numericPrice);
                  }}
                  onValidityChange={setIsSponsorshipSelectionValid}
                  getTranslation={getTranslation}
                />
              </>
            )}

            {currentStep === 'payment' && totalPrice > 0 && user && (
              <>
                <div className={styles.ticketsHeader}>
                  <h2 className={styles.sectionTitle}>{getTranslation('events.paymentInformation')}</h2>
                  <Button
                    variant="ghost"
                    size="small"
                    onClick={() => setCurrentStep('sponsorship')}
                    className={styles.backButton}
                  >
                    {getTranslation('common.back')}
                  </Button>
                </div>
                <PaymentForm
                  attendees={selectedAttendees}
                  totalPrice={totalPrice}
                  currency="TL"
                  userID={user.id}
                  userInfo={{
                    name: user.name,
                    lastname: user.lastname,
                    email: user.email,
                    phone: selectedAttendees.length > 0 ? selectedAttendees[0].phone : ''
                  }}
                  onPaymentSuccess={handlePaymentSuccess}
                  onPaymentError={handlePaymentError}
                  getTranslation={getTranslation}
                />
              </>
            )}
          </div>
        )}

        {/* Mobile Summary Section - Shown when sponsorship offers are selected */}
        {showSponsorshipView && currentStep === 'sponsorship' && selectedAttendees.length > 0 && (
          <div className={`${styles.section} ${styles.mobileSummarySection}`}>
            <div className={styles.infoCard}>
              <h3 className={styles.infoCardTitle}>{getTranslation('events.orderSummary')}</h3>
              <div className={styles.summaryInfo}>
                <div className={styles.summaryRow}>
                  <span>{getTranslation('events.totalTickets')}:</span>
                  <span className={styles.summaryValue}>{selectedAttendees.length}</span>
                </div>
                <div className={styles.summaryRow}>
                  <span>{getTranslation('events.totalPrice')}:</span>
                  <span className={styles.summaryValue}>
                    {(() => {
                      const numericPrice = typeof totalPrice === 'number' ? totalPrice : Number(totalPrice) || 0;
                      return numericPrice === 0 
                        ? getTranslation('events.free')
                        : `${numericPrice.toLocaleString('en-US')} TL`;
                    })()}
                  </span>
                </div>
              </div>
              <Button
                variant="primary"
                size="large"
                onClick={() => {
                  // Validate attendees before proceeding
                  if (sponsorshipOffersRef.current) {
                    const validation = sponsorshipOffersRef.current.validate();
                    if (!validation.ok) {
                      // Validation errors are already shown in the component
                      if (validation.msg) {
                        toastError(validation.msg);
                      }
                      return;
                    }
                  }
                  
                  // Check if we have attendees selected
                  if (selectedAttendees.length === 0) {
                    toastError(getTranslation('events.fillAllFields'));
                    return;
                  }
                  
                  // Ensure totalPrice is a number for comparison
                  const numericPrice = typeof totalPrice === 'number' ? totalPrice : Number(totalPrice) || 0;
                  
                  if (numericPrice === 0) {
                    // Free tickets - show confirmation modal directly (skip checkout step)
                    setShowFreeTicketConfirmModal(true);
                  } else {
                    // Paid tickets - go directly to payment step (skip checkout)
                    setCurrentStep('payment');
                  }
                }}
                className={styles.checkoutButton}
                disabled={(() => {
                  // Check if we have attendees
                  if (selectedAttendees.length === 0) {
                    return true;
                  }

                  return !isSponsorshipSelectionValid;
                })()}
              >
                {(typeof totalPrice === 'number' ? totalPrice : Number(totalPrice) || 0) === 0
                  ? getTranslation('events.confirmPurchase')
                  : getTranslation('events.proceedToPayment')
                }
              </Button>
            </div>
          </div>
        )}

        {/* Event Details - Hidden when choosing tickets */}
        {currentStep === 'details' && (
          <>
            {/* Sidebar Content - Mobile only (Community, Event Title, Date/Location) */}
            <section className={`${styles.section} ${styles.mobileSidebarContent}`}>
              <div className={styles.eventDetailsContainer}>
                {/* Community Section */}
                {(event.channelDetails?.title || event.channelTitle) && event.channelID && (
                  <div 
                    className={`${styles.communitySection} ${isChannelTitleTwoLines ? styles.communitySectionTitleTwoLines : ''}`}
                    onClick={() => {
                      const communityTitle = event.channelDetails?.title || event.channelTitle;
                      if (event.channelID) {
                        navigate(generateCommunityUrl(communityTitle, event.channelID));
                      }
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    {event.channelDetails?.logo || event.channelLogo ? (
                      <img
                        src={event.channelDetails?.logo || event.channelLogo}
                        alt={event.channelDetails?.title || event.channelTitle || ''}
                        className={styles.communityIcon}
                        onError={() => {}}
                      />
                    ) : (
                      <ChannelInitialIcon 
                        channelName={event.channelDetails?.title || event.channelTitle} 
                        className={styles.communityIcon}
                      />
                    )}
                    <div className={styles.communityInfo}>
                      <h3 className={styles.communityName}>{event.channelDetails?.title || event.channelTitle}</h3>
                      {isChannelAdmin ? (
                        <button 
                          className={styles.editEventLink}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditEvent();
                          }}
                        >
                          {getTranslation('communities.editEvent') || 'Etkinliği Düzenle'}
                        </button>
                      ) : (
                        <button 
                          className={isChannelJoined ? styles.joinedCommunityLink : styles.joinCommunityLink}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!token || !user) {
                              toastError(getTranslation('home.loginRequired') || 'Please login to join communities');
                              // Redirect to login with current URL as redirect parameter
                              const currentPath = location.pathname;
                              const redirectUrl = encodeURIComponent(currentPath);
                              navigate(`/login?redirect=${redirectUrl}`);
                              return;
                            }
                            if (isChannelJoined) {
                              handleLeaveChannel();
                            } else {
                              handleJoinChannel();
                            }
                          }}
                        >
                          {isChannelJoined ? (
                            <>
                              {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
                              {/* @ts-ignore - react-icons compatibility with React 19 */}
                              <HiCheck className={styles.checkIcon} />
                              <span>{getTranslation('communities.joined')}</span>
                            </>
                          ) : (
                            getTranslation('communities.joinCommunity')
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Event Title Section */}
                <div className={styles.eventTitleSection}>
                  <h2 className={styles.eventTitleText}>{eventTitle}</h2>
                  <div className={styles.actionButtons}>
                    <button 
                      className={`${styles.bookmarkButton} ${isSaved ? styles.bookmarkButtonSaved : ''}`}
                      aria-label={isSaved ? getTranslation('home.eventRemoved') : getTranslation('home.eventSaved')}
                      onClick={async () => {
                        if (event) {
                          await toggleBookmark(event);
                        }
                      }}
                    >
                      {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
                      {/* @ts-ignore - react-icons compatibility with React 19 */}
                      <HiBookmark className={styles.bookmarkIcon} />
                    </button>
                    <button 
                      className={styles.shareButton}
                      aria-label={getTranslation('common.share')}
                      onClick={async () => {
                        const shareUrl = `${window.location.origin}${generateEventShareUrl(String(event.eventID))}`;
                        const shareText = `${getTranslation('events.shareText', { eventName: eventTitle })}\n${shareUrl}`;
                        if (navigator.share) {
                          try {
                            await navigator.share({
                              title: eventTitle,
                              text: shareText,
                            });
                          } catch (error) {
                            // User cancelled or error occurred, do nothing
                          }
                        } else {
                          try {
                            await navigator.clipboard.writeText(shareText);
                            toastSuccess(getTranslation('common.linkCopied'));
                          } catch (error) {
                            // Clipboard API failed, could show error toast here if needed
                          }
                        }
                      }}
                    >
                      {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
                      {/* @ts-ignore - react-icons compatibility with React 19 */}
                      <HiShare className={styles.shareIcon} />
                    </button>
                  </div>
                </div>

                {/* Date/Time and Location Cards */}
                <div className={styles.infoCardsRow}>
                  <div 
                    className={styles.infoItemCard}
                    onClick={handleDownloadICSClick}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className={styles.iconWrapper}>
                      {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
                      {/* @ts-ignore - react-icons compatibility with React 19 */}
                      <HiCalendar className={styles.infoIcon} />
                    </div>
                    <div className={styles.infoItemContent}>
                      <div className={styles.infoItemText}>
                        {(() => {
                          const startDate = new Date(event.eventStartDateTime * 1000);
                          const endDate = new Date(event.eventEndDateTime * 1000);
                          const isSameDay = startDate.toDateString() === endDate.toDateString();
                          
                          if (isSameDay) {
                            return `${formatDateOnly(event.eventStartDateTime, currentLanguage)}, ${formatTimeOnly(event.eventStartDateTime).replace(':', '.')} - ${formatTimeOnly(event.eventEndDateTime).replace(':', '.')}`;
                          } else {
                            return `${formatDateOnly(event.eventStartDateTime, currentLanguage)} ${formatTimeOnly(event.eventStartDateTime).replace(':', '.')} - ${formatDateOnly(event.eventEndDateTime, currentLanguage)} ${formatTimeOnly(event.eventEndDateTime).replace(':', '.')}`;
                          }
                        })()}
                      </div>
                      <div className={styles.infoItemSubtext}>
                        {getTranslation('events.clickToAddToCalendar') || 'Click to add to calendar'}
                      </div>
                    </div>
                  </div>

                  <div
                    className={`${styles.infoItemCard} ${event.isOnline === "1" && !isChannelAdmin && !hasUserTickets ? styles.infoItemCardDisabled : ''}`}
                    onClick={() => {
                      if (event.isOnline === "1" && event.website && (isChannelAdmin || hasUserTickets)) {
                        // For online events, open the event website (only for admins or ticket holders)
                        const websiteUrl = event.website.startsWith('http://') || event.website.startsWith('https://')
                          ? event.website
                          : `https://${event.website}`;
                        window.open(websiteUrl, '_blank', 'noopener,noreferrer');
                        return;
                      }
                      if (hasCoordinates && latitude !== null && longitude !== null) {
                        const googleMapsUrl = `https://www.google.com/maps?q=${latitude},${longitude}`;
                        window.open(googleMapsUrl, '_blank');
                      }
                    }}
                    style={{ cursor: (event.isOnline === "1" && event.website && (isChannelAdmin || hasUserTickets)) || hasCoordinates ? 'pointer' : 'default' }}
                  >
                    <div className={styles.iconWrapper}>
                      {event.isOnline === "1" ? (
                        <>
                          {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
                          {/* @ts-ignore - react-icons compatibility with React 19 */}
                          <HiGlobeAlt className={styles.infoIcon} />
                        </>
                      ) : (
                        <>
                          {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
                          {/* @ts-ignore - react-icons compatibility with React 19 */}
                          <HiLocationMarker className={styles.infoIcon} />
                        </>
                      )}
                    </div>
                    <div className={styles.infoItemContent}>
                      <div className={styles.infoItemText}>
                        {event.isOnline === "1"
                          ? getTranslation('events.onlineEvent')
                          : (event.locationAddress || (locationDefinition && locationDefinition.includes(' / ')
                            ? locationDefinition.split(' / ')[0]
                            : (locationDefinition ? locationDefinition.split(',')[0] : (event.locationAddressDefinition || getTranslation('events.locationTBD')))))}
                      </div>
                      <div className={styles.infoItemSubtext}>
                        {event.isOnline === "1"
                          ? (isChannelAdmin || hasUserTickets
                            ? (getTranslation('events.goToEventWebsite') || 'Go to event website')
                            : (getTranslation('events.getTicketToAccess') || 'Get a ticket to access the event link'))
                          : (getTranslation('events.clickToViewLocation') || 'Click to view location')}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Buy Ticket Section - Mobile only */}
            <section className={`${styles.section} ${styles.mobileTicketSection}`}>
              <div className={styles.infoCard}>
                <div className={styles.infoCardTitleRow}>
                  <p className={styles.infoCardTitle}>
                    {participationCardTitle}
                  </p>
                  {event?.isPublic !== undefined && (
                    <span className={styles.eventVisibilityPill}>
                      {event.isPublic === "1"
                        ? getTranslation('events.publicEvent')
                        : getTranslation('events.privateEvent')}
                    </span>
                  )}
                </div>
                {loading || participantsLoading ? (
                  <LoadingSkeleton variant="text" width={200} height={28} className={styles.participantCountSkeleton} />
                ) : (
                  <h3 className={styles.participantCount}>
                    {isEventEnded 
                      ? `${participantCount} ${getTranslation('events.peopleAttended') || 'kişi katıldı'}`
                      : `${getTranslation('events.participants')}: ${participantCount}`}
                  </h3>
                )}
                <p className={styles.ticketMessage}>{participationMessage}</p>
                {!isEventEnded && (
                  <ParticipantAvatars
                    participants={eventParticipants}
                    onClick={() => setShowParticipantModal(true)}
                    loading={participantsLoading}
                    getTranslation={getTranslation}
                    participantCount={participantCount}
                  />
                )}
                <Button
                  variant="primary"
                  size="large"
                  className={styles.reserveButton}
                  onClick={handleParticipationAction}
                  disabled={isParticipationActionDisabled}
                >
                  {participationActionLabel}
                </Button>
              </div>
            </section>

            {/* Description */}
            <section className={`${styles.section} ${styles.descriptionSection}`}>
              <div className={styles.descriptionCard}>
                <h2 className={styles.sectionTitle}>{getTranslation('events.description')}</h2>
                <div className={`${styles.descriptionWrapper} ${showFullDescription || !isLongDescription ? styles.descriptionExpanded : styles.descriptionCollapsed}`}>
                  <div
                    className={styles.descriptionText}
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtml(prepareHtmlContent(event.description || ''))
                    }}
                  />
                  {isLongDescription && !showFullDescription && (
                    <div className={styles.descriptionFade} />
                  )}
                </div>
                {isLongDescription && (
                  <button
                    className={styles.showMore}
                    onClick={() => setShowFullDescription(!showFullDescription)}
                  >
                    {showFullDescription ? getTranslation('events.showLess') : getTranslation('events.showMore')}
                  </button>
                )}
              </div>
            </section>


            {/* Map Section */}
            {event.isOnline === "0" && (
              <section className={`${styles.section} ${styles.addressSection}`}>
                <div className={styles.mapSection}>
                  <div className={styles.mapInfo}>
                    <p className={styles.mapSubheading}>{getTranslation('events.address')}</p>
                    {event.locationAddress && (
                      <h2 className={styles.mapTitle}>{event.locationAddress}</h2>
                    )}
                    {event.locationAddressDefinition && event.locationAddressDefinition !== event.locationAddress && (
                      <p className={styles.mapFullAddress}>{event.locationAddressDefinition}</p>
                    )}
                  </div>
                  <div className={styles.mapContainer}>
                    {hasCoordinates ? (
                      <GoogleMap 
                        latitude={latitude!} 
                        longitude={longitude!}
                        address={event.locationAddress}
                      />
                    ) : (
                      <div className={styles.mapPlaceholder}>
                        <p>{getTranslation('events.coordinatesNotAvailable')}</p>
                        {event.locationAddress && (
                          <p style={{ fontSize: '12px', marginTop: '8px', color: '#6b7280' }}>
                            {getTranslation('events.addressLabel')} {event.locationAddress}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </section>
            )}

            {/* Speakers */}
            {sortedSpeakers && sortedSpeakers.length > 0 && (
              <section className={`${styles.section} ${styles.speakersSection}`}>
                <div className={styles.speakersWrapper}>
                  <h2 className={styles.sectionTitle}>{getTranslation('events.speakers')}</h2>
                  <div className={styles.speakersGrid}>
                    {sortedSpeakers.map((speaker) => {
                      const linkedinUrl = speaker.socialMediaLinks?.linkedin || speaker.linkedin;
                      const normalizedLinkedInUrl = normalizeLinkedInUrl(linkedinUrl);
                      const hasLinkedIn = !!normalizedLinkedInUrl;
                      
                      return (
                        <div 
                          key={speaker.speakerID} 
                          className={`${styles.speakerCard} ${hasLinkedIn ? styles.speakerCardLinkedIn : ''}`}
                          onClick={() => {
                            if (hasLinkedIn && normalizedLinkedInUrl) {
                              window.open(normalizedLinkedInUrl, '_blank', 'noopener,noreferrer');
                            }
                          }}
                          style={{ cursor: hasLinkedIn ? 'pointer' : 'default' }}
                        >
                          {speaker.image ? (
                            <img
                              src={speaker.image}
                              alt={`${speaker.name} ${speaker.lastname}`}
                              className={styles.speakerImage}
                              onError={() => {}}
                            />
                          ) : (
                            <ChannelInitialIcon 
                              channelName={`${speaker.name} ${speaker.lastname}`}
                              className={styles.speakerImage}
                            />
                          )}
                          <div className={styles.speakerInfo}>
                            <h3 className={styles.speakerName}>
                              {speaker.name} {speaker.lastname}
                            </h3>
                            <p className={styles.speakerTitle}>{speaker.about}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </section>
            )}

            {/* Sponsors */}
            {event.sponsors && event.sponsors.length > 0 && (() => {
              const groupedSponsors = event.sponsors.reduce((acc, sponsor) => {
                const type = sponsor.sponsorshipType || getTranslation('events.other');
                if (!acc[type]) {
                  acc[type] = [];
                }
                acc[type].push(sponsor);
                return acc;
              }, {} as Record<string, Array<typeof event.sponsors[0]>>);

              // Check if there are any sponsor tickets available
              const hasSponsorTickets = event.tickets && Array.isArray(event.tickets) && event.tickets.some(
                ticket => ticket.type.toLowerCase() === 'sponsor'
              );

              return (
                <section className={`${styles.section} ${styles.sponsorsSection}`}>
                  <div className={styles.sponsorsWrapper}>
                    <div className={styles.sponsorsHeader}>
                      <h2 className={styles.sectionTitle}>{getTranslation('events.sponsors')}</h2>
                      {hasSponsorTickets && (
                        <Button 
                          variant="secondary" 
                          size="small" 
                          className={styles.sponsorButton}
                  onClick={() => {
                    if (!token || !user) {
                      toastError(getTranslation('home.loginRequired') || 'Please login to become a sponsor');
                      // Redirect to login with event page URL and section query parameter
                      const basePath = `/events/${eventPath}`;
                      const redirectUrl = encodeURIComponent(`${basePath}?section=sponsorships`);
                      navigate(`/login?redirect=${redirectUrl}`);
                      return;
                    }
                    setCurrentStep('sponsorship');
                  }}
                        >
                          {getTranslation('events.becomeSponsor')}
                        </Button>
                      )}
                    </div>
                    {Object.entries(groupedSponsors).map(([type, sponsors]) => (
                      <div key={type} className={styles.sponsorTier}>
                        <h3 className={styles.sponsorTierTitle}>{type}</h3>
                        <div className={styles.sponsors}>
                          {sponsors.map((sponsor) => {
                            const hasLink = !!sponsor.website;
                            const card = (
                              <div
                                key={sponsor.sponsorID}
                                className={`${styles.sponsorCard} ${hasLink ? styles.sponsorCardClickable : ''}`}
                                onClick={hasLink ? () => window.open(sponsor.website!, '_blank', 'noopener,noreferrer') : undefined}
                                role={hasLink ? 'link' : undefined}
                              >
                                {sponsor.image && (
                                  <img
                                    src={sponsor.image}
                                    alt={sponsor.companyTitle}
                                    className={styles.sponsorImage}
                                  />
                                )}
                              </div>
                            );
                            return card;
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              );
            })()}

            {/* Comments - only visible for participants and channel admins */}
            {canViewComments && (
              <section className={`${styles.section} ${styles.commentsSection}`}>
                <EventCommentsSection
                  token={token || null}
                  channelID={event.channelID}
                  eventID={event.eventID}
                  comments={event.comments ?? []}
                  currentUserId={user?.id ?? null}
                  canPost={canViewComments}
                  isChannelAdmin={isChannelAdmin}
                  onRefresh={refreshEvent}
                />
              </section>
            )}
          </>
        )}

        {/* User Tickets Section - Mobile only */}
        {currentStep === 'details' && userTickets.length > 0 && (
          <section className={`${styles.section} ${styles.mobileUserTicketsSection}`}>
            <UserTicketsSidebar
              tickets={userTickets}
              event={event}
              getTranslation={getTranslation}
              onTicketClick={(ticket) => {
                // For now, we can show ticket details or navigate to ticket receipt
                // This can be enhanced later to show a ticket detail modal
              }}
            />
          </section>
        )}
      </div>

      {/* Sidebar - Always visible */}
      <div className={styles.sidebar}>
          <div className={styles.eventDetailsContainer}>
            {/* Community Section */}
            {(event.channelDetails?.title || event.channelTitle) && event.channelID && (
              <div 
                className={`${styles.communitySection} ${isChannelTitleTwoLines ? styles.communitySectionTitleTwoLines : ''}`}
                onClick={() => {
                  const communityTitle = event.channelDetails?.title || event.channelTitle;
                  if (event.channelID) {
                    navigate(generateCommunityUrl(communityTitle, event.channelID));
                  }
                }}
                style={{ cursor: 'pointer' }}
              >
                {event.channelDetails?.logo || event.channelLogo ? (
                  <img
                    src={event.channelDetails?.logo || event.channelLogo}
                    alt={event.channelDetails?.title || event.channelTitle || ''}
                    className={styles.communityIcon}
                    onError={() => {}}
                  />
                ) : (
                  <ChannelInitialIcon 
                    channelName={event.channelDetails?.title || event.channelTitle} 
                    className={styles.communityIcon}
                  />
                )}
                <div className={styles.communityInfo}>
                  <h3 ref={channelTitleRef} className={styles.communityName}>{event.channelDetails?.title || event.channelTitle}</h3>
                  {isChannelAdmin ? (
                    <button 
                      className={styles.editEventLink}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditEvent();
                      }}
                    >
                      {getTranslation('communities.editEvent') || 'Etkinliği Düzenle'}
                    </button>
                  ) : (
                    <button 
                      className={isChannelJoined ? styles.joinedCommunityLink : styles.joinCommunityLink}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!token || !user) {
                          toastError(getTranslation('home.loginRequired') || 'Please login to join communities');
                          // Redirect to login with current URL as redirect parameter
                          const currentPath = location.pathname;
                          const redirectUrl = encodeURIComponent(currentPath);
                          navigate(`/login?redirect=${redirectUrl}`);
                          return;
                        }
                        if (isChannelJoined) {
                          handleLeaveChannel();
                        } else {
                          handleJoinChannel();
                        }
                      }}
                    >
                      {isChannelJoined ? (
                        <>
                          {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
                          {/* @ts-ignore - react-icons compatibility with React 19 */}
                          <HiCheck className={styles.checkIcon} />
                          <span>{getTranslation('communities.joined')}</span>
                        </>
                      ) : (
                        getTranslation('communities.joinCommunity')
                      )}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Event Title Section */}
            <div className={styles.eventTitleSection}>
              <h2 className={styles.eventTitleText}>{eventTitle}</h2>
              <div className={styles.actionButtons}>
                <button 
                  className={`${styles.bookmarkButton} ${isSaved ? styles.bookmarkButtonSaved : ''}`}
                  aria-label={isSaved ? getTranslation('home.eventRemoved') : getTranslation('home.eventSaved')}
                  onClick={async () => {
                    if (!token || !user) {
                      toastError(getTranslation('home.loginRequired') || 'Please login to save events');
                      // Redirect to login with current URL as redirect parameter
                      const currentPath = location.pathname;
                      const redirectUrl = encodeURIComponent(currentPath);
                      navigate(`/login?redirect=${redirectUrl}`);
                      return;
                    }
                    if (event) {
                      await toggleBookmark(event);
                    }
                  }}
                >
                  {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
                  {/* @ts-ignore - react-icons compatibility with React 19 */}
                  <HiBookmark className={styles.bookmarkIcon} />
                </button>
                <button 
                  className={styles.shareButton}
                  aria-label={getTranslation('common.share')}
                  onClick={async () => {
                    const shareUrl = `${window.location.origin}${generateEventShareUrl(String(event.eventID))}`;
                    const shareText = `${getTranslation('events.shareText', { eventName: eventTitle })}\n${shareUrl}`;
                    if (navigator.share) {
                      try {
                        await navigator.share({
                          title: eventTitle,
                          text: shareText,
                        });
                      } catch (error) {
                        // User cancelled or error occurred, do nothing
                      }
                    } else {
                      try {
                        await navigator.clipboard.writeText(shareText);
                        toastSuccess(getTranslation('common.linkCopied'));
                      } catch (error) {
                        // Clipboard API failed, could show error toast here if needed
                      }
                    }
                  }}
                >
                  {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
                  {/* @ts-ignore - react-icons compatibility with React 19 */}
                  <HiShare className={styles.shareIcon} />
                </button>
              </div>
            </div>

            {/* Date/Time and Location Cards */}
            <div className={styles.infoCardsRow}>
              <div 
                className={styles.infoItemCard}
                onClick={handleDownloadICSClick}
                style={{ cursor: 'pointer' }}
              >
                <div className={styles.iconWrapper}>
                  {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
                  {/* @ts-ignore - react-icons compatibility with React 19 */}
                  <HiCalendar className={styles.infoIcon} />
                </div>
                <div className={styles.infoItemContent}>
                  <div className={styles.infoItemText}>
                    {(() => {
                      const startDate = new Date(event.eventStartDateTime * 1000);
                      const endDate = new Date(event.eventEndDateTime * 1000);
                      const isSameDay = startDate.toDateString() === endDate.toDateString();
                      
                      if (isSameDay) {
                        return `${formatDateOnly(event.eventStartDateTime, currentLanguage)}, ${formatTimeOnly(event.eventStartDateTime).replace(':', '.')} - ${formatTimeOnly(event.eventEndDateTime).replace(':', '.')}`;
                      } else {
                        return `${formatDateOnly(event.eventStartDateTime, currentLanguage)} ${formatTimeOnly(event.eventStartDateTime).replace(':', '.')} - ${formatDateOnly(event.eventEndDateTime, currentLanguage)} ${formatTimeOnly(event.eventEndDateTime).replace(':', '.')}`;
                      }
                    })()}
                  </div>
                  <div className={styles.infoItemSubtext}>
                    {getTranslation('events.clickToAddToCalendar') || 'Click to add to calendar'}
                  </div>
                </div>
              </div>

              <div
                className={`${styles.infoItemCard} ${event.isOnline === "1" && !isChannelAdmin && !hasUserTickets ? styles.infoItemCardDisabled : ''}`}
                onClick={() => {
                  if (event.isOnline === "1" && event.website && (isChannelAdmin || hasUserTickets)) {
                    // For online events, open the event website (only for admins or ticket holders)
                    const websiteUrl = event.website.startsWith('http://') || event.website.startsWith('https://')
                      ? event.website
                      : `https://${event.website}`;
                    window.open(websiteUrl, '_blank', 'noopener,noreferrer');
                    return;
                  }
                  if (hasCoordinates && latitude !== null && longitude !== null) {
                    const googleMapsUrl = `https://www.google.com/maps?q=${latitude},${longitude}`;
                    window.open(googleMapsUrl, '_blank');
                  }
                }}
                style={{ cursor: (event.isOnline === "1" && event.website && (isChannelAdmin || hasUserTickets)) || hasCoordinates ? 'pointer' : 'default' }}
              >
                <div className={styles.iconWrapper}>
                  {event.isOnline === "1" ? (
                    <>
                      {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
                      {/* @ts-ignore - react-icons compatibility with React 19 */}
                      <HiGlobeAlt className={styles.infoIcon} />
                    </>
                  ) : (
                    <>
                      {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
                      {/* @ts-ignore - react-icons compatibility with React 19 */}
                      <HiLocationMarker className={styles.infoIcon} />
                    </>
                  )}
                </div>
                <div className={styles.infoItemContent}>
                  <div className={styles.infoItemText}>
                    {event.isOnline === "1"
                      ? getTranslation('events.onlineEvent')
                      : (event.locationAddress || (locationDefinition && locationDefinition.includes(' / ')
                        ? locationDefinition.split(' / ')[0]
                        : (locationDefinition ? locationDefinition.split(',')[0] : (event.locationAddressDefinition || getTranslation('events.locationTBD')))))}
                  </div>
                  <div className={styles.infoItemSubtext}>
                    {event.isOnline === "1"
                      ? (isChannelAdmin || hasUserTickets
                        ? (getTranslation('events.goToEventWebsite') || 'Go to event website')
                        : (getTranslation('events.getTicketToAccess') || 'Get a ticket to access the event link'))
                      : (getTranslation('events.clickToViewLocation') || 'Click to view location')}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Ticket Section - Show when on details step */}
          {currentStep === 'details' && (
            <div className={styles.infoCard}>
              <div className={styles.infoCardTitleRow}>
                <p className={styles.infoCardTitle}>
                  {participationCardTitle}
                </p>
                {event?.isPublic !== undefined && (
                  <span className={`${styles.eventVisibilityPill} ${event.isPublic === "1" ? styles.public : styles.private}`}>
                    {event.isPublic === "1"
                      ? getTranslation('events.publicEvent')
                      : getTranslation('events.privateEvent')}
                  </span>
                )}
              </div>
              {loading || participantsLoading ? (
                <LoadingSkeleton variant="text" width={200} height={28} className={styles.participantCountSkeleton} />
              ) : (
                <h3 className={styles.participantCount}>
                  {isEventEnded 
                    ? `${participantCount} ${getTranslation('events.peopleAttended') || 'kişi katıldı'}`
                    : `${getTranslation('events.participants')}: ${participantCount}`}
                </h3>
              )}
              <p className={styles.ticketMessage}>{participationMessage}</p>
              {!isEventEnded && (
                <ParticipantAvatars
                  participants={eventParticipants}
                  onClick={() => setShowParticipantModal(true)}
                  loading={participantsLoading}
                  getTranslation={getTranslation}
                  participantCount={participantCount}
                />
              )}
              <Button
                variant="primary"
                size="large"
                className={styles.reserveButton}
                onClick={handleParticipationAction}
                disabled={isParticipationActionDisabled}
              >
                {participationActionLabel}
              </Button>
            </div>
          )}

          {/* User Tickets Section - Show when user has tickets */}
          {currentStep === 'details' && userTickets.length > 0 && (
            <UserTicketsSidebar
              tickets={userTickets}
              getTranslation={getTranslation}
              onTicketClick={(ticket) => {
                // For now, we can show ticket details or navigate to ticket receipt
                // This can be enhanced later to show a ticket detail modal
              }}
            />
          )}

          {/* Checkout Summary Section - Shown when tickets are selected (replaces tickets available section) - Desktop only */}
          {(currentStep === 'tickets' || currentStep === 'sponsorship') && selectedAttendees.length > 0 && (
            <div className={`${styles.infoCard} ${styles.desktopSummarySection}`}>
              <h3 className={styles.infoCardTitle}>{getTranslation('events.orderSummary')}</h3>
              <div className={styles.summaryInfo}>
                <div className={styles.summaryRow}>
                  <span>{getTranslation('events.totalTickets')}:</span>
                  <span className={styles.summaryValue}>{selectedAttendees.length}</span>
                </div>
                <div className={styles.summaryRow}>
                  <span>{getTranslation('events.totalPrice')}:</span>
                  <span className={styles.summaryValue}>
                    {(() => {
                      const numericPrice = typeof totalPrice === 'number' ? totalPrice : Number(totalPrice) || 0;
                      return numericPrice === 0 
                        ? getTranslation('events.free')
                        : `${numericPrice.toLocaleString('en-US')} TL`;
                    })()}
                  </span>
                </div>
              </div>
              <Button
                variant="primary"
                size="large"
                onClick={() => {
                  // Validate attendees before proceeding
                  const isSponsorship = currentStep === 'sponsorship';
                  const validationRef = isSponsorship ? sponsorshipOffersRef : ticketSelectionRef;
                  
                  if (validationRef.current) {
                    const validation = validationRef.current.validate();
                    if (!validation.ok) {
                      // Validation errors are already shown in the component
                      if (validation.msg) {
                        toastError(validation.msg);
                      }
                      return;
                    }
                  }
                  
                  // Check if we have attendees selected
                  if (selectedAttendees.length === 0) {
                    toastError(getTranslation('events.fillAllFields'));
                    return;
                  }
                  
                  // Ensure totalPrice is a number for comparison
                  const numericPrice = typeof totalPrice === 'number' ? totalPrice : Number(totalPrice) || 0;
                  
                  if (numericPrice === 0) {
                    // Free tickets - show confirmation modal directly (skip checkout step)
                    setShowFreeTicketConfirmModal(true);
                  } else {
                    // Paid tickets - go directly to payment step (skip checkout)
                    setCurrentStep('payment');
                  }
                }}
                className={styles.checkoutButton}
                disabled={(() => {
                  // Check if we have attendees
                  if (selectedAttendees.length === 0) {
                    return true;
                  }
                  
                  const isSponsorship = currentStep === 'sponsorship';
                  return isSponsorship ? !isSponsorshipSelectionValid : !isTicketSelectionValid;
                })()}
              >
                {(typeof totalPrice === 'number' ? totalPrice : Number(totalPrice) || 0) === 0
                  ? getTranslation('events.confirmPurchase')
                  : getTranslation('events.proceedToPayment')
                }
              </Button>
            </div>
          )}

          {/* Order Summary Section - Shown when on payment step (only for paid tickets) */}
          {currentStep === 'payment' && selectedAttendees.length > 0 && (() => {
            const numericPrice = typeof totalPrice === 'number' ? totalPrice : Number(totalPrice) || 0;
            return numericPrice > 0;
          })() && (
            <div className={styles.infoCard}>
              <h3 className={styles.infoCardTitle}>{getTranslation('events.orderSummary')}</h3>
              <TicketReceipt
                attendees={selectedAttendees}
                totalPrice={totalPrice}
                getTranslation={getTranslation}
              />
            </div>
          )}

        </div>

      {/* Free Ticket Confirmation Modal */}
      <Modal
        isOpen={showFreeTicketConfirmModal}
        onClose={() => setShowFreeTicketConfirmModal(false)}
        variant="confirm"
        title={getTranslation('events.confirmPurchase')}
        message={`${getTranslation('events.confirmPurchase')}: ${selectedAttendees.length} ${getTranslation('events.tickets')}?`}
        confirmText={getTranslation('events.confirmPurchase')}
        cancelText={getTranslation('common.cancel')}
        onConfirm={handleFreeTicketsPurchase}
        confirmVariant="primary"
      />

      {/* Success Modal */}
      <Modal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        variant="alert"
        title={getTranslation('common.success')}
        message={getTranslation('events.ticketsPurchasedSuccessfully')}
        alertText={getTranslation('common.close')}
      />

      {/* Join Channel Confirmation Modal */}
      <Modal
        isOpen={showJoinChannelConfirmModal}
        onClose={() => setShowJoinChannelConfirmModal(false)}
        variant="confirm"
        title={getTranslation('communities.confirmJoin')}
        message=""
        confirmText={getTranslation('communities.joinCommunity')}
        cancelText={getTranslation('common.cancel')}
        onConfirm={confirmJoinChannel}
        confirmVariant="primary"
      />

      {/* Leave Channel Confirmation Modal */}
      <Modal
        isOpen={showLeaveChannelConfirmModal}
        onClose={() => setShowLeaveChannelConfirmModal(false)}
        variant="confirm"
        title={getTranslation('communities.confirmLeave')}
        message=""
        confirmText={getTranslation('communities.leaveCommunity')}
        cancelText={getTranslation('common.cancel')}
        onConfirm={confirmLeaveChannel}
        confirmVariant="primary"
      />

      {/* Calendar Service Selection Modal */}
      {calendarModal.shouldRender && (
        <div className={`${styles.modalOverlay} ${calendarModal.isClosing ? styles.modalOverlayClosing : ''}`} onClick={() => setShowCalendarDownloadModal(false)} onAnimationEnd={calendarModal.onAnimationEnd}>
          <div className={`${styles.calendarModalContent} ${calendarModal.isClosing ? styles.calendarModalContentClosing : ''}`} onClick={(e) => e.stopPropagation()}>
            <div className={styles.calendarModalHeader}>
              <h3 className={styles.calendarModalTitle}>{getTranslation('events.addToCalendar') || getTranslation('events.downloadCalendar') || 'Add to Calendar'}</h3>
              <button
                className={styles.calendarCloseButton}
                onClick={() => setShowCalendarDownloadModal(false)}
                aria-label={getTranslation('common.close') || 'Close'}
              >
                {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
                {/* @ts-ignore - react-icons compatibility with React 19 */}
                <HiX className={styles.calendarCloseIcon} />
              </button>
            </div>
            <div className={styles.calendarModalBody}>
              <p className={styles.calendarModalMessage}>
                {getTranslation('events.addToCalendarDescription') || 'Choose how you\'d like to add this event to your calendar'}
              </p>
              <div className={styles.calendarServiceList}>
                <button
                  className={styles.calendarServiceButton}
                  onClick={handleGoogleCalendar}
                  aria-label="Add to Google Calendar"
                >
                  <div className={styles.calendarServiceIcon}>
                    {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
                    {/* @ts-ignore - react-icons compatibility with React 19 */}
                    <HiCalendar />
                  </div>
                  <span>{getTranslation('events.googleCalendar') || 'Google Calendar'}</span>
                </button>
                <button
                  className={styles.calendarServiceButton}
                  onClick={handleOutlookCalendar}
                  aria-label="Add to Outlook"
                >
                  <div className={styles.calendarServiceIcon}>
                    {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
                    {/* @ts-ignore - react-icons compatibility with React 19 */}
                    <HiCalendar />
                  </div>
                  <span>{getTranslation('events.outlookCalendar') || 'Outlook'}</span>
                </button>
                <button
                  className={styles.calendarServiceButton}
                  onClick={handleYahooCalendar}
                  aria-label="Add to Yahoo Calendar"
                >
                  <div className={styles.calendarServiceIcon}>
                    {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
                    {/* @ts-ignore - react-icons compatibility with React 19 */}
                    <HiCalendar />
                  </div>
                  <span>{getTranslation('events.yahooCalendar') || 'Yahoo Calendar'}</span>
                </button>
                <button
                  className={`${styles.calendarServiceButton} ${styles.calendarServiceButtonPrimary}`}
                  onClick={handleConfirmDownloadICS}
                  aria-label="Download .ics file"
                >
                  <div className={styles.calendarServiceIcon}>
                    {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
                    {/* @ts-ignore - react-icons compatibility with React 19 */}
                    <HiDownload />
                  </div>
                  <span>{getTranslation('events.downloadICS') || 'Download .ics file'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Participant List Modal */}
        <ParticipantListModal
          isOpen={showParticipantModal}
          onClose={() => setShowParticipantModal(false)}
          participants={eventParticipants}
          tickets={event?.tickets || []}
          getTranslation={getTranslation}
        />
      </div>
    </>
  );
};

export default EventDetailPage;
